import { z } from 'zod';

/** Parse a Vite env string ("true"/"false"/"1"/"0") into a real boolean. */
const booleanFromString = (defaultValue: boolean) =>
  z
    .union([z.string(), z.boolean()])
    .optional()
    .transform((val) => {
      if (typeof val === 'boolean') return val;
      if (val === undefined || val === '') return defaultValue;
      return ['true', '1', 'yes', 'on'].includes(val.trim().toLowerCase());
    });

/**
 * Environment variable schema with Zod validation
 */
const envSchema = z.object({
  // Application
  VITE_APP_NAME: z.string().min(1, {
    message: 'VITE_APP_NAME е задължителна променлива',
  }),
  VITE_APP_VERSION: z
    .string()
    .regex(/^\d+\.\d+\.\d+$/, {
      message:
        'VITE_APP_VERSION трябва да е във формат X.Y.Z (например 0.26.0)',
    })
    .default('0.1.0'),

  // API Configuration
  VITE_API_BASE_URL: z
    .string()
    .url({
      message: 'VITE_API_BASE_URL трябва да е валиден URL адрес',
    })
    .default('http://localhost:3000/api'),
  VITE_API_TIMEOUT: z.coerce
    .number()
    .positive({
      message: 'VITE_API_TIMEOUT трябва да е положително число',
    })
    .default(30000),

  // Monitoring Configuration - LogRocket
  VITE_LOGROCKET_APP_ID: z.string().optional(),
  VITE_ENABLE_SESSION_REPLAY: booleanFromString(false),
  VITE_ENABLE_ERROR_TRACKING: booleanFromString(false),

  // Monitoring Configuration - Sentry
  VITE_SENTRY_DSN: z.string().optional(),
  VITE_SENTRY_TRACES_SAMPLE_RATE: z.coerce.number().min(0).max(1).optional(),
  VITE_SENTRY_SEND_DEFAULT_PII: booleanFromString(false),
  VITE_ENABLE_PERFORMANCE_MONITORING: booleanFromString(false),

  // Feature Flags
  VITE_ENABLE_ANALYTICS: booleanFromString(false),
  VITE_ENABLE_DEBUG: booleanFromString(false),

  // OAuth Configuration
  // Gates the social sign-in buttons on /login and /register. Defaults to
  // false — OAuth providers are not configured for the initial production
  // release. The /auth/callback route stays registered so enabling the flag
  // later requires no code change.
  VITE_ENABLE_OAUTH: booleanFromString(false),

  // Supabase Configuration
  // REQUIRED — Supabase is the only data layer (mock mode was removed
  // app-wide). The anon/publishable key is safe to ship to the browser; the
  // service-role key MUST NEVER be placed here.
  VITE_SUPABASE_URL: z.string().url({
    message: 'VITE_SUPABASE_URL трябва да е валиден URL адрес',
  }),
  VITE_SUPABASE_ANON_KEY: z.string().min(1, {
    message: 'VITE_SUPABASE_ANON_KEY е задължителна променлива',
  }),

  // Realtime Configuration
  // Kill-switch for Supabase realtime (postgres_changes/presence)
  // subscriptions. Realtime is a core requirement (live notifications,
  // presence), so it defaults to true; set to false only to disable
  // websockets in an emergency.
  VITE_USE_SUPABASE_REALTIME: booleanFromString(true),

  // P2P Chat Signaling
  // Origins of the WebRTC chat's signaling worker: the HTTP one serves
  // `/turn-creds`, the WebSocket one `/signal/:room` and `/presence`.
  //
  // These two used to be the only VITE_ variables in the app that skipped this
  // schema — `getSignalUrls()` read them straight off `import.meta.env` behind
  // a cast, with `http://localhost:3001` / `ws://localhost:3001` as silent
  // fallbacks. They are inlined at build time, so a typo in .env.production
  // could not be noticed at runtime either: the production bundle simply dialed
  // a dev machine's loopback and the chat never connected, with nothing logged
  // anywhere. Validating here turns that into a boot failure carrying the
  // variable's name, which is where a mistake in a build-time constant belongs.
  //
  // The defaults are kept, and are exactly where the local Hono signaling
  // server in `server/` listens (`PORT ?? 3001`), so an UNSET variable still
  // means "use the local dev server" — only a MALFORMED one now fails. The
  // protocol refinements catch the mistake `.url()` cannot: swapping the two
  // values, which is otherwise two perfectly valid URLs in the wrong slots.
  VITE_P2P_SIGNAL_URL: z
    .string()
    .url({
      message: 'VITE_P2P_SIGNAL_URL трябва да е валиден URL адрес',
    })
    .refine((value) => /^https?:\/\//.test(value), {
      message: 'VITE_P2P_SIGNAL_URL трябва да започва с http:// или https://',
    })
    .default('http://localhost:3001'),
  VITE_P2P_WS_URL: z
    .string()
    .url({
      message: 'VITE_P2P_WS_URL трябва да е валиден URL адрес',
    })
    .refine((value) => /^wss?:\/\//.test(value), {
      message: 'VITE_P2P_WS_URL трябва да започва с ws:// или wss://',
    })
    .default('ws://localhost:3001'),

  // Build Info (optional)
  VITE_BUILD_TIME: z.string().optional(),
  VITE_COMMIT_SHA: z.string().optional(),

  // Vite Built-in (automatically provided by Vite)
  MODE: z.enum(['development', 'production', 'test']).default('development'),
  DEV: z.boolean(),
  PROD: z.boolean(),
  SSR: z.boolean().default(false),
});

/**
 * Environment variables type
 */
export type Env = z.infer<typeof envSchema>;

/**
 * Validate and parse environment variables
 */
function validateEnv(): Env {
  try {
    const parsed = envSchema.parse({
      VITE_APP_NAME: import.meta.env.VITE_APP_NAME,
      VITE_APP_VERSION: import.meta.env.VITE_APP_VERSION,
      VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
      VITE_API_TIMEOUT: import.meta.env.VITE_API_TIMEOUT,
      VITE_LOGROCKET_APP_ID: import.meta.env.VITE_LOGROCKET_APP_ID,
      VITE_ENABLE_SESSION_REPLAY: import.meta.env.VITE_ENABLE_SESSION_REPLAY,
      VITE_ENABLE_ERROR_TRACKING: import.meta.env.VITE_ENABLE_ERROR_TRACKING,
      VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
      VITE_SENTRY_TRACES_SAMPLE_RATE: import.meta.env
        .VITE_SENTRY_TRACES_SAMPLE_RATE,
      VITE_SENTRY_SEND_DEFAULT_PII: import.meta.env
        .VITE_SENTRY_SEND_DEFAULT_PII,
      VITE_ENABLE_PERFORMANCE_MONITORING: import.meta.env
        .VITE_ENABLE_PERFORMANCE_MONITORING,
      VITE_ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS,
      VITE_ENABLE_DEBUG: import.meta.env.VITE_ENABLE_DEBUG,
      VITE_ENABLE_OAUTH: import.meta.env.VITE_ENABLE_OAUTH,
      VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
      VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
      VITE_USE_SUPABASE_REALTIME: import.meta.env.VITE_USE_SUPABASE_REALTIME,
      VITE_P2P_SIGNAL_URL: import.meta.env.VITE_P2P_SIGNAL_URL,
      VITE_P2P_WS_URL: import.meta.env.VITE_P2P_WS_URL,
      VITE_BUILD_TIME: import.meta.env.VITE_BUILD_TIME,
      VITE_COMMIT_SHA: import.meta.env.VITE_COMMIT_SHA,
      MODE: import.meta.env.MODE,
      DEV: import.meta.env.DEV,
      PROD: import.meta.env.PROD,
      SSR: import.meta.env.SSR,
    });

    return parsed;
  } catch (error) {
    if (error instanceof z.ZodError) {
      const formattedErrors = error.issues.map((issue) => {
        const path = issue.path.join('.');
        return `  - ${path}: ${issue.message}`;
      });

      const errorMessage = [
        'Невалидна конфигурация на environment променливи:',
        '',
        ...formattedErrors,
        '',
        'Моля, проверете вашия .env файл и уверете се, че всички задължителни променливи са зададени коректно.',
        '',
        'За повече информация вижте .env.example',
      ].join('\n');

      // In development, show detailed error
      if (import.meta.env.DEV) {
        console.error(errorMessage);
        console.error('\nПодробна информация за грешката:', error);
      }

      throw new Error(errorMessage);
    }

    throw error;
  }
}

/**
 * Validated environment variables
 * Use this instead of import.meta.env for type safety and validation
 */
export const env = validateEnv();

/**
 * Helper functions for common environment checks
 */
export const isDevelopment = env.DEV;
export const isProduction = env.PROD;
export const isTest = env.MODE === 'test';
export const isAnalyticsEnabled = env.VITE_ENABLE_ANALYTICS;
export const isDebugEnabled = env.VITE_ENABLE_DEBUG;

/**
 * OAuth / social sign-in feature flag.
 * When false (default) the social sign-in buttons are hidden on the login and
 * register pages. Flip `VITE_ENABLE_OAUTH=true` once the OAuth providers are
 * configured in Supabase.
 */
export const isOAuthEnabled = env.VITE_ENABLE_OAUTH;

/**
 * Realtime Subscriptions
 *
 * Kill-switch for Supabase realtime. Defaults to on — realtime delivery is a
 * functional requirement (live notifications, presence). Consumers (the
 * realtime hooks) must treat this value as the single source of truth.
 */
export const isRealtimeEnabled = env.VITE_USE_SUPABASE_REALTIME;

/*
 * NO STARTUP BANNER, DELIBERATELY.
 *
 * This module used to end with a module-scope `console.log` of the whole
 * configuration, guarded by `isDevelopment && isDebugEnabled`. Both halves of
 * that guard are open by default — `.env.development` sets
 * `VITE_ENABLE_DEBUG=true` — so it fired on every `npm run dev` for every
 * contributor, and it fired at IMPORT time: `env` is imported by the Supabase
 * client, the API client and the monitoring providers, so the dump was the
 * first thing in the console before anything had happened worth reading.
 *
 * It also printed `VITE_LOGROCKET_APP_ID` into a log people paste into issues
 * and screen-share. Importing a config module must have no side effect; the
 * ZodError branch in `validateEnv` above is the one thing here that may write
 * to the console, and only when the configuration is actually broken.
 */
